<template>
    <div>
        <h4>{{ $t('monitoring.tabs_label__financial_report') }}</h4>
        <p>{{ $t('monitoring.subtitle__financial_report') }}</p>
        <h4><b>{{ $t('monitoring.title__financial_report_reminder') }}</b></h4>
        <div class="report-info-box">
            <p>{{ $t('monitoring.info_box__financial_report_reminder_title') }}</p>
            <p>{{ $t('monitoring.info_box__financial_report_reminder_line_1') }}</p>
            <p>{{ $t('monitoring.info_box__financial_report_reminder_line_2') }}</p>
            <p>{{ $t('monitoring.info_box__financial_report_reminder_line_3') }}</p>
        </div>
        <div class="col-lg-4">
            <!-- Start & End date -->
            <div class="row">
                <!-- Starting date -->
                <div class="col-lg-6">
                    <base-input
                        name="report.start_date"
                        v-model="MonitoringForm.report.start_date"
                        :error="getError('report.start_date')"
                        v-validate="'required'"
                        :data-vv-as="$t('monitoring.field_title__start_date')"
                        :placeholder="$t('monitoring.field_title__start_date')"
                        >
                        <label>{{$t('monitoring.field_title__start_date')}}</label>
                        <el-date-picker
                            type="date"
                            v-model="startDate"
                            :placeholder="$t('monitoring.field_title__start_date')"
                            >
                        </el-date-picker>
                    </base-input>
                </div>

                <!-- Ending date -->
                <div class="col-lg-6">
                    <base-input
                        name="report.end_date"
                        v-model="MonitoringForm.report.end_date"
                        :placeholder="$t('monitoring.field_title__end_date')"
                        :error="getError('report.end_date')"
                        v-validate="'required'"
                        :data-vv-as="$t('monitoring.field_title__end_date')"
                        >
                        <label>{{$t('monitoring.field_title__end_date')}}</label>
                        <el-date-picker
                            type="date"
                            v-model="endDate"
                            :placeholder="$t('monitoring.field_title__end_date')"
                            >
                        </el-date-picker>
                    </base-input>
                </div>
            </div>
        </div>
        <upload-file
            v-if="MonitoringForm.initialized"
            :key="'financial.report'"
            v-model="MonitoringForm.report.files.financial.report"
            section="financial.report"
            class="upload-report-box"
            :file-data="MonitoringForm.report.files.financial.report"
            :is-editable="MonitoringForm.isEditable"
            :model-name="'MonitoringReport'"
            >
            <div class="row">
                <div class="col-lg-12 upload-report-inner-box">
                    <h4>{{ $t('monitoring.button_title__upload_report') }}</h4>
                </div>
            </div>
        </upload-file>

        <h4 class="mt-supporting-docs-title"><b>{{ $t('monitoring.title__supporting_documents') }}</b></h4>
        <a :href="guidanceFile[$i18n.locale]"
            class="ml-2 btn btn-simple btn-primary"
            target="_blank"
            >
            {{$t('monitoring.download_guidance_reporting_documents')}} <i class="tim-icons icon-cloud-download-93 ml-2"></i>
        </a>
        <base-button
            v-if="!MonitoringForm.report.show_supporting_documents"
            type="primary"
            class="add-supporting-documents"
            @click="showSupportingDocuments"
            >
            {{ $t('monitoring.button_title__add_supporting_documents') }}
        </base-button>

        <div class="row monitoring-support-documents" v-if="MonitoringForm.report.show_supporting_documents">
            <div class="col-12 col-xl-10" v-bind:class="{'msd-expanded': (expandedSelector == group_title)}" v-for="(group, group_title, group_index) in MonitoringForm.report.supporting_docs" :key="group_title">
                <h3 class="card-title" @click="expandedSelector = group_title">{{ $t(group_title) }}</h3>
                <div class="msd-group-inner">
                    <radio :name="'type_of_cost_' + group_index"
                        value="monitoring.supporting_docs_type_simplified_costs"
                        v-model="group.group_type"
                        @input="changeCostType()"
                        class="float-left"
                        >
                        <label>{{ $t('monitoring.supporting_docs_type_simplified_costs') }}</label>
                    </radio>
                    <radio :name="'type_of_cost_' + group_index"
                        value="monitoring.supporting_docs_type_real_costs"
                        v-model="group.group_type"
                        @input="changeCostType()"
                        >
                        <label>{{ $t('monitoring.supporting_docs_type_real_costs') }}</label>
                    </radio>
                    <div class="msd-subgroup" v-for="(subgroup, subgroup_index) in group.items" :key="subgroup_index">
                        <span class="msd-remove-group tim-icons icon-trash-simple" @click="removeSupportGroup(group, subgroup_index)" v-if="MonitoringForm.isEditable && subgroup.custom_group"></span>
                        <h4 class="card-title">{{ $t(subgroup.title) }}</h4>
                        <div class="form-group file-group" v-for="(field, index) in subgroup.custom_fields" :key="'custom_field_' + index">
                            <input v-if="field._editing" class="msd-field-title" type="text" v-model="field.title" />
                            <span v-else class="file-caption">{{ field.title }}</span>
                            <span v-if="field._editing" class="info-naming" :title="$t('monitoring.help_naming_convention')">i</span>
                            <span class="file-caption">{{ $t(field.document_type) }}</span>
                            <upload-file
                                v-if="MonitoringForm.initialized"
                                v-model="MonitoringForm.report.files.financial['support_document_' + (group_index + '_' + subgroup_index + '_' + field.title)]"
                                class="upload-file"
                                :section="'financial.support_document_' + (group_index + '_' + subgroup_index + '_' + field.title)"
                                :file-data="MonitoringForm.report.files.financial['support_document_' + (group_index + '_' + subgroup_index + '_' + field.title)]"
                                :is-editable="MonitoringForm.isEditable"
                                :model-name="'MonitoringReport'"
                                :key="field.title"
                            >
                            </upload-file>
                            <span class="msd-edit-item tim-icons icon-pencil" @click="field._editing = true; $forceUpdate()" v-if="MonitoringForm.isEditable"></span>
                            <span class="msd-remove-item tim-icons icon-trash-simple" @click="removeSupportField(subgroup, group_index, subgroup_index, index, field.title);" v-if="MonitoringForm.isEditable"></span>
                            <div class="msd-document-type" v-if="field._editing">
                                <div class="row">
                                    <div class="col-4">
                                        <h5>{{ 'Select document type' }}</h5>
                                        <radio name="doctype"
                                            v-for="(type, index) in subgroup.document_types[group.group_type]"
                                            :value="type"
                                            v-model="field.document_type"
                                            @input="$forceUpdate()"
                                            :key="type"
                                            >
                                            <label>{{ $t(type) }}</label>
                                        </radio>
                                    </div>
                                    <div class="col-8">
                                        <p><b>{{ $t('monitoring.supporting_docs_help_required_title') }}</b></p>
                                        <p style="white-space: pre-line">{{ $t(group.group_type + '_help_' + group_index + '_' + index) }}</p>
                                    </div>
                                </div>
                                <base-button
                                    v-if="MonitoringForm.isEditable"
                                    type="info"
                                    class="add-new-document"
                                    @click="addGroupField(subgroup.custom_fields, field, index)"
                                    >
                                    Save
                                </base-button>
                            </div>
                        </div>
                        <base-button
                            v-if="MonitoringForm.isEditable"
                            type="primary"
                            class="add-new-document"
                            @click="addNewSupportingDocument(subgroup)"
                            >
                            {{ $t('monitoring.button_title__add_new_document') }}
                        </base-button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
    import {MonitoringService, UserRoles, FileUploadService, MonitoringForm} from '@/services';
    import {EventBus} from '@/event-bus.js';
    import {DatePicker} from 'element-ui';
    import Multiselect from 'vue-multiselect';

    export default
    {
        components:
        {
            MonitoringForm,
            [DatePicker.name]: DatePicker,
            Multiselect,
        },
        data()
        {
            return {
                UserRoles,
                MonitoringForm,
                loggedUser: {},
                guidanceFile: {
                    en: '/files/en/monitoring/2019/LIFE4BEST_Supporting_documents.pdf',
                    fr: '/files/fr/monitoring/2019/LIFE4BEST_Documents_justificatifs.pdf',
                },
                startDate: '',
                endDate: '',
                expandedSelector: '',
                reportId: parseInt(this.$route.params.reportId) || null,
            }
        },
        async beforeMount()
        {
            const vm = this;

            vm.moment = require('moment');
            vm.loggedUser = vm.$store.getters['Auth/loggedUser'];

            if (!MonitoringForm.report.hasOwnProperty('show_supporting_documents'))
            {
                MonitoringForm.report.show_supporting_documents = false;
            }

            vm.startDate = MonitoringForm.report.start_date || '';
            vm.endDate = MonitoringForm.report.end_date || '';
        },
        methods:
        {
            addGroupField(list, field, index) {
                if(list.reduce((reduced, currentField, currentIndex)=>{
                    reduced = (currentField.title === field.title && index !== currentIndex) ? true : reduced
                    return reduced;
                }, false)){
                    window.alert("Field title duplicate. Make sure it is different!")
                } else {
                    field._editing = false;
                    this.$forceUpdate()
                }
            },
            addNewSupportingDocument(subgroup)
            {
                subgroup.custom_fields.push({
                    _editing: true,
                    title: '',
                    document_type: ''
                });

                this.$forceUpdate();
            },

            addSupportingDocumentGroup(group, title, subgroup)
            {
                const count = Object.keys(group).length;

                group.push({
                    custom_group: true,
                    title: this.$t(title).replace(/\s*[0-9]?\s*[0-9]\s*$/, '') + ' ' + (count + 1),
                    fields: [].concat(subgroup.fields),
                    custom_fields: []
                });

                this.$forceUpdate();
            },

            showSupportingDocuments()
            {
                MonitoringForm.report.show_supporting_documents = true;
                this.$forceUpdate();
            },

            changeCostType()
            {
                if (confirm(this.$t('monitoring.confirm__change_cost_type')))
                {
                    this.$forceUpdate();
                }
            },

            removeSupportGroup(group, index)
            {
                if (confirm(this.$t('monitoring.confirm__remove_group')))
                {
                    group.splice(index, 1);
                }
            },

            removeSupportField(subgroup, group_index, subgroup_index, index, field_title)
            {
                if (confirm(this.$t('general.confirm_file_delete_1') + '\n\r' + this.$t('monitoring.confirm__remove_field')))
                {
                    const file = MonitoringForm.report.files.financial['support_document_' + (group_index + '_' + subgroup_index + '_' + field_title)] || {};

                    if (file.id)
                    {
                        FileUploadService.deleteMonitoringReportFile(file.id);
                    }

                    MonitoringForm.report.files.financial['support_document_' + (group_index + '_' + subgroup_index + '_' + field_title)] = {};
                    delete MonitoringForm.report.files.financial['support_document_' + (group_index + '_' + subgroup_index + '_' + field_title)];

                    subgroup.custom_fields.splice(index, 1);
                    this.$forceUpdate();
                }
            },

            getError(field)
            {
                return this.errors.first(field);
            },

            validate()
            {
                return this.$validator.validateAll().then(res =>
                {
                    this.$emit('on-validated', res, this.model);

                    return res;
                });
            },
        },
        watch:
        {
            startDate(new_val)
            {
                MonitoringForm.report.start_date = this.moment(new_val).format('YYYY-MM-DD');
            },
            endDate(new_val)
            {
                MonitoringForm.report.end_date = this.moment(new_val).format('YYYY-MM-DD');
            }
        }
    };
</script>
<style>
.mt-supporting-docs-title
{
    margin-right: 10px;
    margin-bottom: 25px;
    display: inline-block;
}
</style>
